import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { Lock, ArrowRight } from "lucide-react";

/**
 * Friendly account-required prompt for guests trying to access account-only
 * features (saved addresses, order history, favorites, dashboards, etc.).
 *
 * Spec wording: "Create a free account to save your information and track
 * future orders."
 */
export default function GuestPrompt({ title, action }) {
  const location = useLocation();
  // Memoize the navigation-state payload so it isn't a new object reference
  // on every render — keeps the <Link> children stable for React.memo'd trees.
  const linkState = useMemo(() => ({ from: location }), [location]);
  return (
    <div className="px-6 py-16 ap-rise" data-testid="guest-prompt">
      <div className="bg-white border border-[var(--ap-border)] rounded-2xl p-7 text-center max-w-sm mx-auto">
        <div className="w-12 h-12 rounded-full bg-[#fff4ec] text-[var(--ap-primary)] flex items-center justify-center mx-auto">
          <Lock size={20} />
        </div>
        <div className="font-serif text-2xl font-semibold mt-4">{title || "Sign in needed"}</div>
        <p className="text-sm text-[var(--ap-ink-soft)] mt-2 leading-relaxed">
          Create a free account to save your information and track future orders.
        </p>
        <div className="mt-5 space-y-2">
          <Link
            to="/register?role=customer"
            state={linkState}
            data-testid="guest-prompt-register"
            className="block w-full py-3 rounded-full bg-[var(--ap-primary)] hover:bg-[var(--ap-primary-hover-light)] text-white font-semibold text-sm transition-all"
          >
            {action || "Create free account"} <ArrowRight size={14} className="inline ml-1" />
          </Link>
          <Link
            to="/login"
            state={linkState}
            data-testid="guest-prompt-login"
            className="block w-full py-3 rounded-full border border-[var(--ap-border)] text-[var(--ap-ink)] font-semibold text-sm"
          >
            I already have an account
          </Link>
          <Link to="/" data-testid="guest-prompt-back" className="block mt-3 text-xs text-[var(--ap-ink-soft)] hover:text-[var(--ap-primary)]">
            ← Back to browsing
          </Link>
        </div>
      </div>
    </div>
  );
}
